// src/components/Shared.js — reusable UI components

import React from 'react';

// ── Status badge ─────────────────────────────────────────────────
// NOTE: these colors are the invoice/voucher STAGE color-coding system —
// intentionally left untouched by the Medji rebrand. Each stage's color
// is meaningful (not brand decoration), so this stays exactly as-is.
const STATUS_COLORS = {
  stage_1_erp:              'bg-gray-100 text-gray-600',
  stage_2_stock_removal:    'bg-yellow-100 text-yellow-700',
  stage_3_checking:         'bg-blue-100 text-blue-700',
  stage_4_packing:          'bg-purple-100 text-purple-700',
  stage_5_dispatched:       'bg-indigo-100 text-indigo-700',
  stage_6_delivered:        'bg-green-100 text-green-700',
  stage_7_returned_to_office:'bg-teal-100 text-teal-700',
  stage_8_voucher_received: 'bg-green-200 text-green-800',
  complete:                 'bg-green-200 text-green-800',
  cancelled:                'bg-red-100 text-red-700',
  returned:                 'bg-orange-100 text-orange-700',
  reattempt_pending:        'bg-amber-100 text-amber-700',
};

const STATUS_LABELS = {
  stage_1_erp:              'ERP Raised',
  stage_2_stock_removal:    'Stock Removal',
  stage_3_checking:         'Checking',
  stage_4_packing:          'Ready for Delivery',
  stage_5_dispatched:       'In Transit',
  stage_6_delivered:        'Delivered',
  stage_7_returned_to_office:'Person Returned',
  stage_8_voucher_received: 'Voucher Received',
  complete:                 'Complete',
  cancelled:                'Cancelled',
  returned:                 'Returned',
  reattempt_pending:        'Reattempt',
};

export function StatusBadge({ status, picked }) {
  // Status only advances when a stage COMPLETES (that keeps the queue filters correct),
  // so an invoice whose Stock Removal is picked but not finished is still stage_1_erp.
  // Show it as Stock Removal in a lighter shade; the full yellow is used once it finishes.
  const inStockRemoval = picked === true && status === 'stage_1_erp';
  const color = inStockRemoval
    ? 'bg-yellow-50 text-yellow-600 border border-yellow-300'
    : (STATUS_COLORS[status] || 'bg-gray-100 text-gray-600');
  const label = inStockRemoval ? 'Stock Removal' : (STATUS_LABELS[status] || status);
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}>
      {label}
    </span>
  );
}

// ── KPI Card ─────────────────────────────────────────────────────
// NOTE: color variants left untouched — these are semantic (blue=info,
// green=good, amber=warning, red=bad) rather than brand decoration.
export function KpiCard({ label, value, sub, color = 'blue', icon }) {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
    green:  'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
    amber:  'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300',
    red:    'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
    purple: 'bg-purple-50 dark:bg-purple-950/40 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300',
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${colors[color]}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
          <p className="text-3xl font-bold">{value ?? '—'}</p>
          {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
        </div>
        {icon && <span className="text-2xl opacity-40">{icon}</span>}
      </div>
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────
export function Spinner({ size = 'md' }) {
  const sz = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-10 h-10' : 'w-6 h-6';
  return (
    <div className={`${sz} border-2 border-brand-500/30 border-t-brand-700 rounded-full animate-spin`} />
  );
}

// ── Page header ──────────────────────────────────────────────────
export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex justify-between items-start mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subtitle}</p>}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

// ── Alert box ────────────────────────────────────────────────────
// NOTE: type-colors left untouched — error/success/warning/info are
// semantic conventions, not brand decoration.
export function Alert({ type = 'error', message, onClose }) {
  const styles = {
    error:   'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800 text-red-700 dark:text-red-300',
    success: 'bg-green-50 dark:bg-green-950/40 border-green-300 dark:border-green-800 text-green-700 dark:text-green-300',
    warning: 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300',
    info:    'bg-blue-50 dark:bg-blue-950/40 border-blue-300 dark:border-blue-800 text-blue-700 dark:text-blue-300',
  };
  return (
    <div className={`border rounded-lg px-4 py-3 flex justify-between items-center ${styles[type]}`}>
      <span className="text-sm font-medium">{message}</span>
      {onClose && <button onClick={onClose} className="ml-4 text-lg leading-none opacity-60 hover:opacity-100">×</button>}
    </div>
  );
}

// ── Modal ────────────────────────────────────────────────────────
export function Modal({ title, children, onClose, width = 'max-w-lg' }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full ${width} max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center p-6 border-b dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-2xl leading-none">×</button>
        </div>
        <div className="p-6 dark:text-gray-100">{children}</div>
      </div>
    </div>
  );
}

// ── Table ────────────────────────────────────────────────────────
export function Table({ headers, rows, onRowClick, emptyMessage = 'No data found' }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-800 border-b dark:border-gray-700">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={headers.length} className="text-center py-10 text-gray-400 dark:text-gray-500">{emptyMessage}</td></tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={i}
                onClick={() => onRowClick && onRowClick(row)}
                className={`border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}
              >
                {row.cells.map((cell, j) => (
                  <td key={j} className="px-4 py-3 text-gray-700 dark:text-gray-300">{cell}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── Button ───────────────────────────────────────────────────────
export function Btn({ children, onClick, variant = 'primary', size = 'md', disabled, className = '' }) {
  const variants = {
    primary:  'bg-brand-700 dark:bg-brand-500 text-white hover:bg-brand-500 dark:hover:bg-brand-700',
    success:  'bg-green-700 dark:bg-green-700 text-white hover:bg-green-600',
    danger:   'bg-red-600 text-white hover:bg-red-500',
    outline:  'border-2 border-brand-700 dark:border-brand-500 text-brand-700 dark:text-brand-500 hover:bg-brand-700/5 dark:hover:bg-brand-500/10',
    ghost:    'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]} ${sizes[size]} ${className}`}
    >
      {children}
    </button>
  );
}

// ── Input ────────────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div className="mb-4">
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>}
      <input
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
          ${error ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
        {...props}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Select ───────────────────────────────────────────────────────
export function Select({ label, options, error, ...props }) {
  return (
    <div className="mb-4">
      {label && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>}
      <select
        className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
          ${error ? 'border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
        {...props}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Format helpers ───────────────────────────────────────────────
export function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
}

export function formatDuration(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function formatCurrency(amount) {
  if (!amount) return '—';
  return '₹' + parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 });
}
